import React, { useEffect, useState } from 'react'
import fetchAPI from '../../../../common/fetchAPI'
import useToast from '../../../../common/hooks/useToast'

import ConfirmModalContent from '../../../../components/common/ConfirmModalContent'
import Button from '../../../../components/common/Button'
import useModal from '../../../../common/hooks/useModal'
import Toggle from '../../../../components/common/Toggle'
import ClientBuyerSettings from '../ClientBuyerSettings'
import ClientVendorSettings from '../ClientVendorSettings'
import ClientOrgMemberInfo from '../ClientOrgMemberInfo'
import { useParams } from 'react-router-dom'
import useData from '../../../../common/hooks/useData'
import Spinner from '../../../../components/common/Spinner'
import ErrorMessage from '../../../../components/common/ErrorMessage'
import Tag from '../../../../components/common/Tag'
import CheckboxInput from '../../../../components/common/data-form/CheckboxInput'
import SelectInput from '../../../../components/common/data-form/SelectInput'
import SectionContainer from '../../../../components/common/SectionContainer'
import Break from '../../../../components/common/Break'
import useError from '../../../../common/hooks/useError'
import EnableContactFormSetting from './EnableContactFormSetting'

export default function ClientSettings() {
    const { userId } = useParams()

    const { user, userError, userLoading, userMutate } = useData(
        `/v1/users/${userId}?fields=premiumStatusOverride,disableSocialPostPostfix,isPremiumUser,isEmailVerified,disableSocialPostPostfix,enableContactForms`,
        'user',
    )

    const setToast = useToast()
    const { setModal, setModalLoading, isModalLoading } = useModal()

    const [inviteLoading, setInviteLoading] = useState(false)
    const [error, setError] = useError()

    const onSendInviteEmail = async (isBuyer) => {
        setError('')
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    setInviteLoading(true)

                    const { error } = await fetchAPI(
                        `/v1/users/${user.id}/send-invite${
                            isBuyer ? '?buyer=true' : ''
                        }`,
                        {},
                        'POST',
                    )
                    setInviteLoading(false)
                    setModalLoading(false)

                    if (error) {
                        setModal(null)
                        setError(error)

                        return
                    }

                    setModal(null)
                    setToast('Email sent')
                }}
            />,
            'Are you sure you want to send an invite email?',
        )
    }

    const onVerifyEmail = async () => {
        setError('')
        setModal(
            <ConfirmModalContent
                onConfirm={async () => {
                    if (isModalLoading) return
                    setModalLoading(true)
                    const { responseData, error } = await fetchAPI(
                        `/v1/users/${user.id}`,
                        { isEmailVerified: true },
                        'PATCH',
                    )
                    setModalLoading(false)

                    if (error) {
                        setModal(null)
                        setError(error)
                        return
                    }

                    userMutate({ ...user, ...responseData })
                    setModal(null)
                    setToast('Email verified')
                }}
            />,
            'Are you sure you want to mark the email as verified?',
        )
    }

    const onSocialPostfixToggle = async () => {
        setError('')
        const { responseData, error } = await fetchAPI(
            `/v1/users/${user.id}`,
            { disableSocialPostPostfix: !user.disableSocialPostPostfix },
            'PATCH',
        )
        if (error) {
            setModal(null)
            setError(error)
            return
        }

        setToast('Saved')
        userMutate({ ...user, ...responseData })
    }

    if (userLoading) return <Spinner />
    if (userError)
        return (
            <SectionContainer>
                <ErrorMessage>{userError}</ErrorMessage>
            </SectionContainer>
        )

    return (
        <>
            {!!error && (
                <ErrorMessage section onDismiss={() => setError('')}>
                    {error}
                </ErrorMessage>
            )}

            <SectionContainer>
                <Toggle title={'App account'}>
                    <div>
                        <form>
                            <div>
                                Premium status&nbsp;
                                {user.isPremiumUser ? (
                                    <Tag color={'success'}>Premium</Tag>
                                ) : (
                                    <Tag color={'alert'}>Free</Tag>
                                )}
                            </div>
                            <Break />
                        </form>
                    </div>

                    <EnableContactFormSetting
                        user={user}
                        userMutate={userMutate}
                    />

                    {!user.isEmailVerified ? (
                        <Button
                            type="button"
                            text="Mark email as verified"
                            onClick={onVerifyEmail}
                        />
                    ) : (
                        <div>
                            Email status&nbsp;
                            <Tag color={'success'}>Verified</Tag>
                        </div>
                    )}
                    <Button
                        text="Send invite email"
                        onClick={onSendInviteEmail}
                        isLoading={inviteLoading}
                    />
                </Toggle>
            </SectionContainer>

            <ClientVendorSettings />

            <ClientBuyerSettings />

            <ClientOrgMemberInfo />

            <SectionContainer>
                <Toggle title={'Social posts'}>
                    <form>
                        <CheckboxInput
                            label="Disable social post postfix text"
                            id={`has-social-postfix`}
                            onChange={onSocialPostfixToggle}
                            value={user.disableSocialPostPostfix}
                        />
                    </form>
                </Toggle>
            </SectionContainer>
        </>
    )
}
